import React, { useEffect, useRef } from 'react'
import { Moderation } from './moderation-component'
import { useDispatch } from 'react-redux'
import {
  getListedItems,
  setInitialState,
  setQueryParams,
  changeFilterCollapsedAction,
} from '../../store/moderation-page-items/reducer'
import { QueryParams } from './moderation-component/moderation.constants'
import { useNavigate, useLocation } from 'react-router-dom'
import { useModerationPage } from './moderation-page.hooks'
import { LinearProgress } from '@material-ui/core'
import { useAppDialog } from '../../hooks/use-app-dialog'
import { AppDialogVariants } from '../../store/app-dialog/types'
import { ApiError } from '../../services/api/api-error-data'
import { useTranslation } from 'react-i18next'
import { useQuery } from '../../hooks/use-query'
import { nonprofitsApi } from '../../services/api/nonprofits'
import { trackException } from '../../store/app-insights/reducer'
import { selectUser } from '../../store/auth/selectors'
import { useSelector } from 'react-redux'
import { RequestModerationQueryParams } from '../../store/moderation-page-items/contracts'

export const ModerationPage: React.FC = () => {
  const dispatch = useDispatch()
  const urlQuery = useQuery()
  const navigate = useNavigate()
  const location = useLocation()
  const { setAppDialog } = useAppDialog()
  const { t } = useTranslation()
  const user = useSelector(selectUser)
  const {
    itemsList,
    loading,
    query,
    totalPages,
    needApply,
    firstRender,
    isFilterCollapsed,
    error,
  } = useModerationPage()

  /* Scrolled Component */
  const scrolledElement = useRef<HTMLDivElement>(null)

  /* isScrolled Method */
  const isScrollToTop = (): boolean => {
    if (scrolledElement.current === null) {
      return false
    }

    return scrolledElement.current!.getBoundingClientRect().top < -304
  }

  /* Error handler */
  useEffect(() => {
    if (error) {
      setAppDialog({
        isShow: true,
        variant: AppDialogVariants.error,
        message: new ApiError(error).getErrorMessage(t),
        title: t('home.loadingErrorDialog.title'),
      })
    }
  }, [error])

  /* Scroll to up when items loaded */
  useEffect(() => {
    isScrollToTop() && window.scrollTo(0, 0)
  }, [itemsList])

  /* Change Query Handler*/
  const changeQueryParamsAction = (
    data: QueryParams,
    isApply?: boolean,
    isStartPagingInBegin?: boolean
  ) => {
    dispatch(setQueryParams({ ...data, isApply, isStartPagingInBegin }))
    if (!firstRender && isApply) {
      const {
        pageIndex,
        pageSize,
        moderationState,
      } = data

      const _pageSize = pageSize ? pageSize : query.pageSize
      const _moderationState =
        moderationState !== undefined ? moderationState : query.moderationState

      // eslint-disable-next-line functional/no-let
      let _pageIndex = pageIndex ? pageIndex : query.pageIndex
      _pageIndex = isStartPagingInBegin ? 1 : _pageIndex

      navigate(
        `/moderation?pageIndex=${_pageIndex}&pageSize=${_pageSize}&moderationState=${_moderationState}`
      )
    }
  }

  /*Set Query Data From Url*/
  const setQueryDataFromUrl = async () => {
    try {
      const fromUrlNonProfitExternalId = urlQuery.get('nonProfitExternalId')
      if (fromUrlNonProfitExternalId) {
        await nonprofitsApi
          .getInfoByExternalId(fromUrlNonProfitExternalId)
          .catch((e) => {
            const { response } = e
            if (response && response.status === 404) {
              setAppDialog({
                variant: AppDialogVariants.error,
                isShow: true,
                title: t('home.error.404.title'),
                message: t('home.error.404.message'),
                actions: null,
              })
            } else {
              setAppDialog({
                variant: AppDialogVariants.error,
                isShow: true,
                title: t('home.error.unknown.title'),
                message: t('home.error.unknown.message'),
                actions: null,
              })
            }
            dispatch(trackException({ e, source: 'getInfoByExternalId' }))
          })
      }
      const fromUrlPageIndex = urlQuery.get('pageIndex')
      const fromUrlPageSize = urlQuery.get('pageSize')
      const fromUrlShowSold = (() => {
        const urlParam = urlQuery.get('showSold')
        return urlParam === 'true'
      })()
      const fromUrlModerationState = urlQuery.get('moderationState')

      const priceValue = [0, 20000] as number[]

      const queryObject = {
        pageIndex: fromUrlPageIndex
          ? Number(fromUrlPageIndex)
          : query.pageIndex,
        priceValue: priceValue,
        pageSize: fromUrlPageSize ? Number(fromUrlPageSize) : query.pageSize,
        showSold: fromUrlShowSold ? fromUrlShowSold : query.showSold,
        moderationState: fromUrlModerationState ? fromUrlModerationState : '',
      }

      if (firstRender) {
        changeQueryParamsAction({ ...queryObject })
      }

      const requestParams: any = {
        pageSize: queryObject.pageSize,
        pageIndex: queryObject.pageIndex - 1,
        showSold: queryObject.showSold,
        moderationState: queryObject.moderationState,
      }

      dispatch(getListedItems(requestParams))
    } catch (e) {
      dispatch(trackException({ e, source: 'setSearchQueryDataFromUrl' }))
    }
  }

  /*Cancel Handler*/
  const cancelHandler = () => {
    dispatch(setInitialState())
    navigate('/')
  }

  useEffect(() => {
    setQueryDataFromUrl()
  }, [location])

  useEffect(() => {
    return () => {
      dispatch(setInitialState())
    }
  }, [])

  /*Filter Collapsed Handler*/
  const changeFilterCollapsedHandler = (value: boolean) => {
    dispatch(changeFilterCollapsedAction(value))
  }

  return (
    <>
      {user.role === 1 && (
        <>
          <LinearProgress style={{ visibility: loading ? 'visible' : 'hidden' }} />
          <div ref={scrolledElement}>
            <Moderation
              isFilterCollapsed={isFilterCollapsed}
              loading={loading}
              firstRender={firstRender}
              query={query as RequestModerationQueryParams}
              totalPages={totalPages}
              cancelHandler={cancelHandler}
              changeQueryParamsAction={changeQueryParamsAction}
              itemsList={itemsList}
              changeFilterCollapsedHandler={changeFilterCollapsedHandler}
              needApply={needApply}
            />
          </div>
        </>
      )}
      {user.role === 0 || user.role === undefined && (
        <div>
          {t('moderationPage.notModerator')}
        </div>
      )}
      </>
    )
}
